import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Subscription } from 'rxjs';
import type { Insight, InsightItem } from './insight.model';
import { InsightApiService } from './insight-api.service';
import { InsightFirestoreService } from './insight-firestore.service';

interface InsightState {
  insights: Insight[];
  selectedId: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: InsightState = {
  insights: [],
  selectedId: null,
  loading: false,
  saving: false,
  error: null,
};

export const InsightStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    selected: computed(
      () => store.insights().find((item) => item.id === store.selectedId()) ?? null,
    ),
  })),
  withMethods((store, firestore = inject(InsightFirestoreService), api = inject(InsightApiService)) => {
    let subscription: Subscription | null = null;
    return {
      load(identity: string): void {
        subscription?.unsubscribe();
        patchState(store, { loading: true, error: null });
        subscription = firestore.watchVisible(identity).subscribe({
          next: (insights) =>
            patchState(store, (current) => ({
              insights,
              selectedId:
                current.selectedId && insights.some((item) => item.id === current.selectedId)
                  ? current.selectedId
                  : (insights[0]?.id ?? null),
              loading: false,
            })),
          error: (error) => patchState(store, { loading: false, error: String(error) }),
        });
      },
      select(id: string | null): void {
        patchState(store, { selectedId: id, error: null });
      },
      async updateItem(
        insight: Insight,
        item: InsightItem,
        reviewStatus: string,
        reviewNote: string,
        followUpStatus: string,
        followUpReference: string,
      ): Promise<void> {
        if (!item.itemId) return;
        patchState(store, { saving: true, error: null });
        try {
          await api.updateItem(
            insight.id,
            item.itemId,
            reviewStatus,
            reviewNote,
            followUpStatus,
            followUpReference,
          );
        } catch (error) {
          patchState(store, { error: error instanceof Error ? error.message : String(error) });
        } finally {
          patchState(store, { saving: false });
        }
      },
      destroy(): void {
        subscription?.unsubscribe();
      },
    };
  }),
  withHooks({ onDestroy: (store) => store.destroy() }),
);
